import React from 'react';
import { createPortal } from 'react-dom';
import { Boxes, GitBranch, Info, Package } from 'lucide-react';
import { useOrderDetailsPedidoDetalhe } from '../hooks/useOrderDetailsPedidoDetalhe';
import { formatPedidoTimelineDateBr } from '../utils/orderDetailsModalUtils';
import { Odv2HoverTooltip } from './Odv2HoverTooltip';
import { OrderDetailsV2MenuModal } from './OrderDetailsV2MenuModal';
import './OrderDetailsPedidoDetalheModal.css';

function formatInt(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return String(Math.trunc(n));
}

function StockTh({ label, tooltip }) {
  return (
    <th className="odv2-pedido-detalhe-th-est">
      {label}
      <Odv2HoverTooltip text={tooltip} placement="above">
        <span className="odv2-pedido-detalhe-info" tabIndex={0} aria-label={tooltip}>
          <Info size={10} strokeWidth={2} aria-hidden />
        </span>
      </Odv2HoverTooltip>
    </th>
  );
}

function PedidoDetalheTitleMeta({ pedido, nota }) {
  const ped = String(pedido ?? '').trim();
  if (!ped) return null;

  return (
    <span className="odv2-pedido-detalhe-title-meta">
      <span className="odv2-pedido-detalhe-header-item">
        <span className="odv2-pedido-detalhe-header-label">Pedido</span>
        <span className="odv2-pedido-detalhe-header-value">{ped}</span>
      </span>
      {nota ? (
        <span className="odv2-pedido-detalhe-header-item">
          <span className="odv2-pedido-detalhe-header-label">Nota</span>
          <span className="odv2-pedido-detalhe-header-value">{nota}</span>
          <span className="odv2-pedido-detalhe-header-hint">nota fiscal</span>
        </span>
      ) : null}
    </span>
  );
}

function PedidoStatusStepper({ items }) {
  if (!items?.length) {
    return <p className="odv2-pedido-detalhe-hint">Sem histórico de status.</p>;
  }

  return (
    <ol className="odv2-pedido-detalhe-stepper" aria-label="Processo do pedido">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const isCurrent = isLast;

        return (
          <li
            key={`${item.TB02130_NOME}-${item.TB02130_DATA}-${index}`}
            className={`odv2-pedido-detalhe-step${isCurrent ? ' odv2-pedido-detalhe-step--current' : ''}`}
          >
            <div className="odv2-pedido-detalhe-step-body">
              <span className="odv2-pedido-detalhe-step-nome">
                {String(item.TB02130_NOME ?? '').trim() || '—'}
              </span>
              <span className="odv2-pedido-detalhe-step-date">
                {formatPedidoTimelineDateBr(item.TB02130_DATA)}
              </span>
              <span className="odv2-pedido-detalhe-step-user">
                {String(item.TB02130_USER ?? '').trim() || '—'}
              </span>
            </div>
            {!isLast ? (
              <span className="odv2-pedido-detalhe-step-line" aria-hidden />
            ) : null}
            <span
              className={`odv2-pedido-detalhe-step-dot${isCurrent ? ' odv2-pedido-detalhe-step-dot--current' : ''}`}
              aria-hidden
            />
          </li>
        );
      })}
    </ol>
  );
}

export function OrderDetailsPedidoDetalheModal({ open, onClose, codigoPedido, portalTarget, native = false }) {
  const cod = String(codigoPedido ?? '').trim();
  const { data, loading, error } = useOrderDetailsPedidoDetalhe(cod, open && Boolean(cod), native);

  const modal = (
    <OrderDetailsV2MenuModal
      open={open}
      onClose={onClose}
      title="Detalhes do pedido"
      icon={<Package size={18} strokeWidth={2} aria-hidden />}
      titleId="odv2-pedido-detalhe-title"
      titleSuffix={(
        <PedidoDetalheTitleMeta
          pedido={data?.pedido || cod}
          nota={data?.nota}
        />
      )}
      closeAriaLabel="Fechar detalhes do pedido"
      panelClassName="odv2-pedido-detalhe-panel"
      bodyClassName="odv2-pedido-detalhe-body"
    >
      {loading ? <p className="odv2-pedido-detalhe-hint">Carregando…</p> : null}
      {error ? (
        <p className="odv2-pedido-detalhe-error" role="alert">
          {error}
        </p>
      ) : null}
      {!loading && !error && data ? (
        <>
          {data.produtos?.length > 0 ? (
            <div className="odv2-pedido-detalhe-section">
              <h3 className="order-details-v2-block-title odv2-pedido-detalhe-section-title">
                <Boxes size={20} strokeWidth={2} aria-hidden />
                Produtos
              </h3>
              <div className="odv2-pedido-detalhe-table-wrap">
              <table className="odv2-pedido-detalhe-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Referência</th>
                    <th className="odv2-pedido-detalhe-th-produto">Produto</th>
                    <th>Fabricante</th>
                    <th className="odv2-pedido-detalhe-th-qty">Qtde</th>
                    <StockTh label="E.Com." tooltip="Estoque de comércio" />
                    <StockTh label="E.Imob." tooltip="Estoque Imobilizado" />
                    <StockTh label="E.C.Res." tooltip="Estoque de comércio reservado" />
                    <StockTh label="E.C.Disp." tooltip="Estoque de comércio disponível" />
                  </tr>
                </thead>
                <tbody>
                  {data.produtos.map((row, index) => (
                    <tr key={`${row.codprod}-${index}`}>
                      <td className="odv2-pedido-detalhe-td-mono">{row.codprod ?? '—'}</td>
                      <td>{row.referencia ?? '—'}</td>
                      <td className="odv2-pedido-detalhe-td-produto">
                        <Odv2HoverTooltip text={String(row.produto ?? '').trim() || '—'} placement="above">
                          <span className="odv2-pedido-detalhe-produto">{row.produto ?? '—'}</span>
                        </Odv2HoverTooltip>
                      </td>
                      <td>{row.fabricante ?? '—'}</td>
                      <td className="odv2-pedido-detalhe-td-qty">{formatInt(row.qtde)}</td>
                      <td className="odv2-pedido-detalhe-td-num">{formatInt(row.qtdeCom)}</td>
                      <td className="odv2-pedido-detalhe-td-num">{formatInt(row.qtdeImob)}</td>
                      <td className="odv2-pedido-detalhe-td-num">{formatInt(row.qtdeComRes)}</td>
                      <td className="odv2-pedido-detalhe-td-num">{formatInt(row.qtdeComDisp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          ) : (
            <p className="odv2-pedido-detalhe-hint">Nenhum produto.</p>
          )}

          <div className="odv2-pedido-detalhe-section">
            <h3 className="order-details-v2-block-title odv2-pedido-detalhe-section-title">
              <GitBranch size={20} strokeWidth={2} aria-hidden />
              Processo
            </h3>
            <PedidoStatusStepper items={data.statusHistorico} />
          </div>

          <textarea
            className="odv2-pedido-detalhe-obs-nf"
            readOnly
            rows={3}
            value={String(data.obsNf ?? '').trim() || 'Sem observações de nota fiscal.'}
            aria-label="Observações de nota fiscal"
          />
        </>
      ) : null}
    </OrderDetailsV2MenuModal>
  );

  if (portalTarget) {
    return createPortal(modal, portalTarget);
  }

  return modal;
}
